import { useState } from "react";
import { ChevronRight, Anchor, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ARMADA_SECTORS } from "@/lib/armadaSectors";
import { LANDING, ARMADA } from "@/constants/testIds";

export const ArmadaSectorList = ({ compact = false }) => {
  const [open, setOpen] = useState(null); // sector id
  const [form, setForm] = useState({ name: "", email: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const close = () => { setOpen(null); setForm({ name: "", email: "", notes: "" }); };

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name + email required."); return; }
    setBusy(true);
    try {
      await api.post("/armada/requests", {
        sector_id: open,
        name: form.name,
        email: form.email,
        notes: form.notes,
      });
      toast.success("Signal launched. EIGOO will respond.");
      close();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sectors = compact ? ARMADA_SECTORS.slice(0, 6) : ARMADA_SECTORS;
  const activeSector = ARMADA_SECTORS.find((s) => s.id === open);

  return (
    <>
      <div className="border-t border-[#C9A227]/30 divide-y divide-[#C9A227]/15">
        {sectors.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.id}
              data-testid={ARMADA.sectorCard}
              className="group relative flex flex-col md:flex-row md:items-center gap-5 md:gap-8 py-7 md:py-8 hover:bg-[#C9A227]/5 transition-colors px-2 md:px-6">
              <div className="flex items-center gap-5 flex-1 min-w-0">
                <div className="text-[#C9A227] font-black text-xs tracking-[0.3em] w-8 shrink-0">{s.code}</div>
                <div className="relative w-12 h-12 md:w-14 md:h-14 shrink-0 flex items-center justify-center border border-[#C9A227]/40 bg-black">
                  <Icon className="text-[#C9A227] relative z-10" size={22}/>
                  <div className="absolute inset-0 bg-[#C9A227]/0 group-hover:bg-[#C9A227]/10 transition-colors"/>
                  <div className="absolute -inset-0.5 bg-[#C9A227]/0 group-hover:shadow-[0_0_28px_-4px_rgba(212,175,55,0.55)] transition-shadow"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-white text-xl md:text-2xl font-black uppercase tracking-tight leading-none">{s.name}</h3>
                    <span className={`text-[9px] tracking-[0.3em] uppercase font-bold px-2 py-0.5 border ${
                      s.status === "Active"
                        ? "border-[#C9A227] text-[#C9A227] bg-[#C9A227]/10"
                        : "border-white/20 text-white/50"
                    }`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-white/55 text-sm mt-2 leading-relaxed">{s.tagline}</p>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-white/35 mt-2">{s.territory}</p>
                </div>
              </div>
              <button onClick={() => setOpen(s.id)}
                data-testid={LANDING.armadaRequestBtn}
                className="btn-outline-gold text-[10px] whitespace-nowrap self-start md:self-auto">
                Request Custom Hull <ChevronRight size={12} className="inline -mt-0.5"/>
              </button>
            </div>
          );
        })}
      </div>

      {open && activeSector && (
        <div className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={close} data-testid={ARMADA.requestModal}>
          <div className="glass border border-[#C9A227]/40 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={close} className="absolute right-4 top-4 text-white/60 hover:text-[#C9A227]"><X size={20}/></button>
            <div className="flex items-center gap-3">
              <Anchor className="text-[#C9A227]" size={18}/>
              <span className="label-gold">Request Custom Hull</span>
            </div>
            <h3 className="text-white text-2xl md:text-3xl font-black uppercase tracking-tighter mt-2">{activeSector.name} Sector</h3>
            <p className="text-white/55 text-sm mt-2">{activeSector.tagline}</p>
            <div className="space-y-4 mt-6">
              <div>
                <label className="label-gold">Your Name</label>
                <input className="siren-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid={ARMADA.requestName}/>
              </div>
              <div>
                <label className="label-gold">Email</label>
                <input type="email" className="siren-input" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid={ARMADA.requestEmail}/>
              </div>
              <div>
                <label className="label-gold">Territory You Command (optional)</label>
                <textarea className="siren-input" rows={3} placeholder="Briefly describe your authority in this sector."
                  value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid={ARMADA.requestNotes}/>
              </div>
              <button onClick={submit} disabled={busy} className="btn-gold w-full text-xs" data-testid={ARMADA.requestSubmitBtn}>
                {busy ? "Launching Signal…" : "Launch Signal to EIGOO"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
